import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Game, Difficulty, SudokuGrid, GameMove, CellValue } from '../../../shared/types';
import { SudokuGenerator, SudokuSolver, generateUUID, cloneGrid } from '../lib/sudoku';

interface GameHistory {
  grid: SudokuGrid;
  move: GameMove;
}

interface GameStore {
  // State
  currentGame: Game | null;
  isPaused: boolean;
  isNoteMode: boolean;
  selectedCell: { row: number; col: number } | null;
  history: GameHistory[];
  historyIndex: number;
  timer: number;
  errors: number;
  errorCells: Set<string>;
  cellNotes: Map<string, Set<number>>;
  aiHintCell: { row: number; col: number; value: number } | null;
  
  // Generators
  generator: SudokuGenerator;
  solver: SudokuSolver;
  
  // Actions
  startNewGame: (difficulty: Difficulty) => void;
  restartCurrentGame: () => void;
  loadGame: (game: Game) => void;
  makeMove: (row: number, col: number, value: CellValue) => void;
  selectCell: (row: number, col: number) => void;
  clearCell: (row: number, col: number) => void;
  toggleNoteMode: () => void;
  undo: () => void;
  redo: () => void;
  pauseGame: () => void;
  resumeGame: () => void;
  abandonGame: () => void;
  incrementTimer: () => void;
  useHint: () => void;
  checkSolution: () => boolean;
  reset: () => void;
  toggleCellNote: (row: number, col: number, num: number) => void;
  getCellNotes: (row: number, col: number) => Set<number>;
  setAIHintCell: (row: number, col: number, value: number) => void;
  clearAIHintCell: () => void;
}

const generator = new SudokuGenerator();
const solver = new SudokuSolver();

export const useGameStore = create<GameStore>()(
  persist(
    (set, get) => ({
      // Initial state
      currentGame: null,
      isPaused: false,
      isNoteMode: false,
      selectedCell: null,
      history: [],
      historyIndex: -1,
      timer: 0,
      errors: 0,
      errorCells: new Set(),
      cellNotes: new Map(),
      aiHintCell: null,
      generator,
      solver,

      // Start new game
      startNewGame: (difficulty: Difficulty) => {
        const { puzzle, solution } = generator.generate(difficulty);
        const now = Date.now();
        
        const newGame: Game = {
          id: generateUUID(),
          userId: 0, // Will be set after auth
          puzzle,
          solution,
          currentState: cloneGrid(puzzle),
          difficulty,
          hintsUsed: 0,
          timeElapsed: 0,
          status: 'in_progress',
          createdAt: now,
          updatedAt: now,
        };

        set({
          currentGame: newGame,
          isPaused: false,
          isNoteMode: false,
          selectedCell: null,
          history: [],
          historyIndex: -1,
          timer: 0,
          errors: 0,
          errorCells: new Set(),
          cellNotes: new Map(), // Clear notes
          aiHintCell: null, // Clear AI hint
        });
      },

      // Restart current game (same puzzle, reset state)
      restartCurrentGame: () => {
        const state = get();
        const { currentGame } = state;
        
        if (!currentGame) return;

        const updatedGame: Game = {
          ...currentGame,
          currentState: cloneGrid(currentGame.puzzle), // Reset to initial puzzle
          hintsUsed: 0,
          timeElapsed: 0,
          status: 'in_progress',
          updatedAt: Date.now(),
        };

        set({
          currentGame: updatedGame,
          isPaused: false,
          isNoteMode: false,
          selectedCell: null,
          history: [],
          historyIndex: -1,
          timer: 0,
          errors: 0,
          errorCells: new Set(),
          cellNotes: new Map(), // Clear notes
          aiHintCell: null, // Clear AI hint
        });
      },

      // Load existing game
      loadGame: (game: Game) => {
        set({
          currentGame: game,
          isPaused: false,
          history: [],
          historyIndex: -1,
          timer: game.timeElapsed,
        });
      },

      // Make a move
      makeMove: (row: number, col: number, value: CellValue) => {
        const state = get();
        const { currentGame, errors, errorCells, aiHintCell } = state;
        
        if (!currentGame || currentGame.status !== 'in_progress') return;
        
        // Can't modify puzzle cells
        if (currentGame.puzzle[row][col] !== 0) return;

        const newGrid = cloneGrid(currentGame.currentState);
        newGrid[row][col] = value;

        const cellKey = `${row}-${col}`;
        let newErrors = errors;
        const newErrorCells = new Set(errorCells);

        // Check if the move is correct
        if (value !== 0) {
          const isCorrect = currentGame.solution[row][col] === value;
          
          if (!isCorrect) {
            // Wrong move - always count as error (even if same cell)
            newErrors++;
            newErrorCells.add(cellKey);
          } else {
            // Correct move - remove from errors if it was there
            if (errorCells.has(cellKey)) {
              newErrorCells.delete(cellKey);
            }
          }
        } else {
          // Clearing cell - remove from errors
          if (errorCells.has(cellKey)) {
            newErrorCells.delete(cellKey);
          }
        }

        // Clear AI hint if user set value in the hinted cell
        let newAIHintCell = aiHintCell;
        if (aiHintCell && aiHintCell.row === row && aiHintCell.col === col) {
          newAIHintCell = null;
        }

        // Clear notes when value is entered
        const newNotes = new Map(state.cellNotes);
        if (value !== 0) {
          // Clear notes when entering a value
          newNotes.delete(cellKey);
        }

        // Add to history
        const newHistory = state.history.slice(0, state.historyIndex + 1);
        newHistory.push({
          grid: cloneGrid(currentGame.currentState),
          move: { row, col, value },
        });

        const updatedGame: Game = {
          ...currentGame,
          currentState: newGrid,
          updatedAt: Date.now(),
        };

        // Check if game is lost (3 errors)
        if (newErrors >= 3) {
          updatedGame.status = 'abandoned';
        }
        // Check if game is complete
        else if (generator.isComplete(newGrid)) {
          updatedGame.status = 'completed';
          updatedGame.completedAt = Date.now();
          updatedGame.timeElapsed = state.timer;
        }

        set({
          currentGame: updatedGame,
          history: newHistory,
          historyIndex: newHistory.length - 1,
          errors: newErrors,
          errorCells: newErrorCells,
          aiHintCell: newAIHintCell,
          cellNotes: newNotes,
        });
      },

      // Select cell
      selectCell: (row: number, col: number) => {
        set({ selectedCell: { row, col } });
      },

      // Clear cell
      clearCell: (row: number, col: number) => {
        get().makeMove(row, col, 0);
      },

      // Toggle note mode
      toggleNoteMode: () => {
        set(state => ({ isNoteMode: !state.isNoteMode }));
      },

      // Undo
      undo: () => {
        const state = get();
        if (state.historyIndex < 0 || !state.currentGame) return;

        const previousState = state.history[state.historyIndex];
        
        set({
          currentGame: {
            ...state.currentGame,
            currentState: cloneGrid(previousState.grid),
          },
          historyIndex: state.historyIndex - 1,
        });
      },

      // Redo
      redo: () => {
        const state = get();
        const nextIndex = state.historyIndex + 1;
        
        if (nextIndex >= state.history.length || !state.currentGame) return;

        const nextState = state.history[nextIndex];
        const newGrid = cloneGrid(nextState.grid);
        newGrid[nextState.move.row][nextState.move.col] = nextState.move.value;

        set({
          currentGame: {
            ...state.currentGame,
            currentState: newGrid,
          },
          historyIndex: nextIndex,
        });
      },

      // Pause game
      pauseGame: () => {
        set({ isPaused: true });
      },

      // Resume game
      resumeGame: () => {
        set({ isPaused: false });
      },

      // Abandon game
      abandonGame: () => {
        const { currentGame, timer } = get();
        if (!currentGame) return;

        set({
          currentGame: {
            ...currentGame,
            status: 'abandoned',
            timeElapsed: timer,
            updatedAt: Date.now(),
          },
        });
      },

      // Increment timer
      incrementTimer: () => {
        const { isPaused, currentGame } = get();
        if (isPaused || !currentGame || currentGame.status !== 'in_progress') return;
        
        set(state => ({ timer: state.timer + 1 }));
      },

      // Use hint
      useHint: () => {
        const { currentGame } = get();
        if (!currentGame || currentGame.status !== 'in_progress') return;

        const hint = solver.getHint(currentGame.currentState);
        if (!hint) return;

        set({
          currentGame: {
            ...currentGame,
            hintsUsed: currentGame.hintsUsed + 1,
          },
          selectedCell: { row: hint.row, col: hint.col },
        });
      },

      // Check solution
      checkSolution: () => {
        const { currentGame } = get();
        if (!currentGame) return false;

        return solver.validate(currentGame.currentState);
      },

      // Reset
      reset: () => {
        set({
          currentGame: null,
          isPaused: false,
          isNoteMode: false,
          selectedCell: null,
          history: [],
          historyIndex: -1,
          timer: 0,
          errors: 0,
          errorCells: new Set(),
          cellNotes: new Map(),
        });
      },

      // Toggle cell note
      toggleCellNote: (row: number, col: number, num: number) => {
        const state = get();
        const cellKey = `${row}-${col}`;
        const newNotes = new Map(state.cellNotes);
        const cellNotes = newNotes.get(cellKey) || new Set<number>();
        
        if (cellNotes.has(num)) {
          cellNotes.delete(num);
        } else {
          cellNotes.add(num);
        }
        
        if (cellNotes.size === 0) {
          newNotes.delete(cellKey);
        } else {
          newNotes.set(cellKey, cellNotes);
        }
        
        set({ cellNotes: newNotes });
      },

      // Get cell notes
      getCellNotes: (row: number, col: number) => {
        const state = get();
        const cellKey = `${row}-${col}`;
        return state.cellNotes.get(cellKey) || new Set<number>();
      },

      // Set AI hint cell
      setAIHintCell: (row: number, col: number, value: number) => {
        set({ aiHintCell: { row, col, value } });
      },

      // Clear AI hint cell
      clearAIHintCell: () => {
        set({ aiHintCell: null });
      },
    }),
    {
      name: 'sudoku-game-storage',
      partialize: (state) => ({
        currentGame: state.currentGame,
        timer: state.timer,
        history: state.history,
        historyIndex: state.historyIndex,
        // Convert Map to array for serialization
        cellNotes: Array.from(state.cellNotes.entries()).map(([key, set]) => [key, Array.from(set)]),
      }),
      // Convert array back to Map on rehydration
      onRehydrateStorage: () => (state) => {
        if (state && Array.isArray(state.cellNotes)) {
          state.cellNotes = new Map(
            state.cellNotes.map(([key, arr]: [string, number[]]) => [key, new Set(arr)])
          );
        }
      },
    }
  )
);

